/**
 * Ricoh Maps engine wiring for the Estate campus map preview (capability tokens).
 *
 * SLIM PORT. workplace-settings is an admin surface: it renders the campus map
 * to preview building placement, NOT to book desks. The engine's DI contract is
 * fully port-driven and EVERY capability token ships a `providedIn: 'root'`
 * no-op default, so we supply only the two ports campus rendering genuinely
 * needs and let the rest fall back to those defaults:
 *
 *   • bundleSource → MapsApiService — supplies the campus geometry + building
 *     bundles (loadCampusMap / loadVenueGroups). Its default returns nothing, so
 *     without it campus mode renders its "No buildings" empty state.
 *   • config.mapboxToken — the map is blank without it.
 *
 * Everything else (availability, alignment, platform, realtime, campus-host,
 * telemetry, status-colours) keeps the engine's built-in no-op default: campus +
 * buildings render UNCOLOURED, which is exactly what an estate admin needs. When
 * a real availability/booking layer is ported later, pass it to provideRicohMaps
 * here — no engine change required. See bead indoor_mapping_poc-c4n.
 *
 * WHY ROUTE-SCOPED, NOT app.config. provideRicohMaps() lives in the light
 * '@ricoh-maps/angular/core' entry (tokens only, no Mapbox/Three), but '/core'
 * still carries turf-dependent geometry helpers, so importing it from app.config
 * pulls turf into the INITIAL chunk. Attaching these providers to the lazy campus
 * route instead keeps '/core' (and the whole engine) out of the initial bundle —
 * it loads only when an admin opens the map. Route `providers` accept the
 * EnvironmentProviders that provideRicohMaps() returns.
 *
 * NOTE: the component-scoped engine SERVICE list (fresh Mapbox/Three/layers per
 * map) is RICOH_MAPS_ENGINE_PROVIDERS, imported from the heavy root entry
 * directly by CampusMapComponent — it must NOT live here, or a route importing
 * this file would eagerly pull the engine in.
 */
import { provideRicohMaps } from '@ricoh-maps/angular/core';
import type { EnvironmentProviders } from '@angular/core';

import { MapsApiService } from '../../../core/services/maps-api.service';
import { environment } from '../../../../environments/environment';

/** Capability-token wiring for the campus map preview. Attach to the campus
 *  route's `providers` so the engine stays lazy. */
export const provideCampusMapEngine = (): EnvironmentProviders =>
  provideRicohMaps({
    config: {
      mapboxToken: environment.MAPBOX_KEY,
      exposeMapForTesting: !environment.production,
    },
    bundleSource: MapsApiService,
  });
